// WALLET-P1: money on the Billing screens. The API speaks integer micros of USD (1 USD =
// 1,000,000 micros); clients see cents (1 minor = 10,000 micros), rounded half away from zero,
// all in integers. Inputs ("12.50", "-3") become micros the same way, never through floats.
import { toMinor } from "../costs/costs-page";
import { formatMoney } from "../format";

export const MICROS_PER_MINOR = 10_000;
export const MICROS_PER_DOLLAR = 1_000_000;

/** Micros → cents, rounded half away from zero, integer math only. */
export function microsToMinor(micros: number): number {
  const sign = micros < 0 ? -1 : 1;
  const abs = Math.abs(micros);
  const whole = (abs - (abs % MICROS_PER_MINOR)) / MICROS_PER_MINOR;
  const rest = abs % MICROS_PER_MINOR;
  return sign * (whole + (rest * 2 >= MICROS_PER_MINOR ? 1 : 0)) || 0;
}

/** "$1,234.50" from micros. */
export function dollars(micros: number, opts: { whole?: boolean } = {}): string {
  return formatMoney(microsToMinor(micros), "USD", opts);
}

/** A balance as a client sees it: never below $0.00. */
export function balanceText(micros: number | null): string {
  if (micros === null) return "—";
  return dollars(Math.max(0, micros));
}

/** "+$50.00" / "−$3.20": a signed ledger amount. */
export function signedDollars(micros: number): string {
  const minor = microsToMinor(micros);
  if (minor === 0) return formatMoney(0, "USD");
  return `${minor > 0 ? "+" : "−"}${formatMoney(Math.abs(minor), "USD")}`;
}

/** "12.50" → 12,500,000 micros; null when not an amount with at most two decimals. */
export function parseDollars(value: string): number | null {
  const minor = toMinor(value.replace(/^\$/, "").replace(/^-\$/, "-"), "USD");
  return minor === null ? null : minor * MICROS_PER_MINOR;
}

/** A price with up to six decimals ("0.005") → micros; null when not one. Rates only. */
export function parseRate(value: string): number | null {
  const v = value.trim().replace(/^\$/, "").replace(/,/g, "");
  if (!/^\d+(\.\d{1,6})?$/.test(v)) return null;
  const [whole = "0", frac = ""] = v.split(".");
  return Number(whole) * MICROS_PER_DOLLAR + Number(frac.padEnd(6, "0") || "0");
}

/** Micros → "0.005" (a rate as typed: no trailing zeros past the cents). */
export function rateToInput(micros: number): string {
  const whole = (micros - (micros % MICROS_PER_DOLLAR)) / MICROS_PER_DOLLAR;
  const frac = String(micros % MICROS_PER_DOLLAR)
    .padStart(6, "0")
    .replace(/0+$/, "")
    .padEnd(2, "0");
  return `${whole}.${frac}`;
}

/** A rate for the operator's tables: cents when whole cents, else up to six decimals. */
export function rateText(micros: number): string {
  if (micros % MICROS_PER_MINOR === 0) return dollars(micros);
  return `$${rateToInput(micros)}`;
}

/** Micros → "12.50" for an input's initial value. */
export function microsToInput(micros: number): string {
  const minor = microsToMinor(micros);
  const sign = minor < 0 ? "-" : "";
  const abs = Math.abs(minor);
  return `${sign}${(abs - (abs % 100)) / 100}.${String(abs % 100).padStart(2, "0")}`;
}

/** Basis points → "10%" / "12.5%". */
export function bpText(bp: number): string {
  const whole = (bp - (bp % 100)) / 100;
  const rest = bp % 100;
  return rest === 0 ? `${whole}%` : `${whole}.${String(rest).padStart(2, "0").replace(/0$/, "")}%`;
}

/** "12.5" (percent) → 1250 bp; null when not a percent with at most two decimals. */
export function parsePercent(value: string): number | null {
  const v = value.trim().replace(/%$/, "");
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return null;
  const [whole = "0", frac = ""] = v.split(".");
  return Number(whole) * 100 + Number(frac.padEnd(2, "0") || "0");
}

export type LadderRung = { min_amount_micros: number; bonus_bp: number; label: string | null };

/** The ladder's highest rung at or under `amount`, or null. */
export function rungFor(ladder: readonly LadderRung[], amount: number): LadderRung | null {
  let best: LadderRung | null = null;
  for (const r of ladder)
    if (r.min_amount_micros <= amount && (!best || r.min_amount_micros > best.min_amount_micros))
      best = r;
  return best;
}

/**
 * The bonus a top-up of `amount` earns, as the API credits it: amount × bonus_bp / 10,000 in
 * micros, rounded down, then down to a whole cent.
 */
export function bonusFor(ladder: readonly LadderRung[], amount: number): number {
  const rung = rungFor(ladder, amount);
  if (!rung || rung.bonus_bp <= 0) return 0;
  const raw = (BigInt(amount) * BigInt(rung.bonus_bp)) / 10_000n;
  return Number((raw / 10_000n) * 10_000n);
}

/**
 * The most of a checkout an operator can refund now: the largest whole-cent `r` (at most what is
 * left of the payment) with r + clawback(r) ≤ `available`, where refunding r more of a payment
 * claws back floor(bonus × (refunded + r) / amount) − floor(bonus × refunded / amount) of its
 * bonus. All bigint micros; the result is micros.
 */
export function maxRefundMicros(c: {
  amount_micros: number;
  bonus_micros: number;
  refunded_micros: number;
  available_micros: number;
}): number {
  const amount = BigInt(c.amount_micros);
  const bonus = BigInt(c.bonus_micros);
  const refunded = BigInt(c.refunded_micros);
  const available = BigInt(c.available_micros);
  const cent = 10_000n;
  const left = amount - refunded;
  if (left <= 0n || available <= 0n || amount <= 0n) return 0;
  const clawback = (r: bigint) => (bonus * (refunded + r)) / amount - (bonus * refunded) / amount;
  const fits = (cents: bigint) => cents * cent + clawback(cents * cent) <= available;
  let lo = 0n;
  let hi = left / cent;
  while (lo < hi) {
    const mid = (lo + hi + 1n) / 2n;
    if (fits(mid)) lo = mid;
    else hi = mid - 1n;
  }
  return Number(lo * cent);
}
